import { POI_ICONS, FA_ICONS } from './constants';

/**
 * Get icon for POI type
 */
export const getIconForType = (type) => {
  return POI_ICONS[type] || POI_ICONS.default;
};

/**
 * Get FontAwesome icon for POI type
 */
export const getFAIconForType = (type) => {
  return FA_ICONS[type] || FA_ICONS.default;
};

/**
 * Format distance in kilometers
 */
export const formatDistance = (meters) => {
  const km = (meters / 1000).toFixed(2);
  return `${km} km`;
};

/**
 * Format duration in hours and minutes
 */
export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text, maxLength = 40) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Calculate bounding box from coordinates array
 */
export const calculateBounds = (coordinates) => {
  if (!coordinates || coordinates.length === 0) return null;
  
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  
  coordinates.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });
  
  return [[minLng, minLat], [maxLng, maxLat]];
};

const splitTopLevel = (text, delimiter = ',') => {
  const parts = [];
  if (!text) return parts;

  let depth = 0;
  let start = 0;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '(') depth += 1;
    if (char === ')') depth -= 1;

    if (char === delimiter && depth === 0) {
      parts.push(text.slice(start, i).trim());
      start = i + 1;
    }
  }

  const tail = text.slice(start).trim();
  if (tail) parts.push(tail);

  return parts;
};

const stripOuterParens = (text) => {
  const trimmed = (text || '').trim();
  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
};

const parseRingText = (ringText) => {
  return ringText
    .split(',')
    .map(pair => {
      const [lon, lat] = pair.trim().split(/\s+/).map(parseFloat);
      return [lon, lat];
    })
    .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
};

const parsePolygonText = (polygonText) => {
  const text = stripOuterParens(polygonText);
  if (!text) return null;

  const ringTexts = splitTopLevel(text);
  const rings = ringTexts
    .map(ringText => parseRingText(stripOuterParens(ringText)))
    .filter(ring => ring.length > 0);

  return rings.length > 0 ? rings : null;
};

/**
 * Parse WKT POLYGON/MULTIPOLYGON to GeoJSON geometry
 */
export const parseWKTGeometry = (wkt) => {
  if (!wkt || typeof wkt !== 'string') return null;
  const trimmed = wkt.trim();

  if (/^POLYGON/i.test(trimmed)) {
    const body = trimmed.replace(/^POLYGON\s*/i, '');
    const rings = parsePolygonText(body);
    return rings ? { type: 'Polygon', coordinates: rings } : null;
  }

  if (/^MULTIPOLYGON/i.test(trimmed)) {
    let body = trimmed.replace(/^MULTIPOLYGON\s*/i, '');
    body = stripOuterParens(body);
    if (!body) return null;

    const polygonTexts = splitTopLevel(body);
    const polygons = polygonTexts
      .map(polygonText => parsePolygonText(polygonText))
      .filter(polygon => polygon && polygon.length > 0);

    return polygons.length > 0 ? { type: 'MultiPolygon', coordinates: polygons } : null;
  }

  return null;
};

/**
 * Flatten GeoJSON geometry coordinates into [lng, lat] pairs
 */
export const getGeometryCoordinates = (geometry) => {
  if (!geometry || !geometry.coordinates) return [];

  const coordinates = [];
  const collect = (node) => {
    if (!Array.isArray(node)) return;

    if (
      node.length >= 2 &&
      typeof node[0] === 'number' &&
      typeof node[1] === 'number'
    ) {
      coordinates.push([node[0], node[1]]);
      return;
    }

    node.forEach(collect);
  };

  collect(geometry.coordinates);
  return coordinates;
};

/**
 * Decode polyline (Google polyline format)
 */
export const decodePolyline = (encoded) => {
  if (!encoded) return [];
  
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;
  const coordinates = [];
  
  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    
    const deltaLat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += deltaLat;
    
    shift = 0;
    result = 0;
    
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    
    const deltaLng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += deltaLng;
    
    coordinates.push([lng * 1e-5, lat * 1e-5]);
  }
  
  return coordinates;
};

/**
 * Create a debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if a point is valid
 */
export const isValidPoint = (point) => {
  return point && 
         typeof point.lat === 'number' && 
         typeof point.lng === 'number' &&
         !isNaN(point.lat) && 
         !isNaN(point.lng);
};

/**
 * Get POI popup HTML
 */
export const getPoiPopupHTML = (properties) => {
  const { name, type, amenity, status, address, poi_type, center_type } = properties;
  const icon = getIconForType(type || amenity);
  
  let html = `
    <div class="poi-popup">
      <div class="poi-popup-header">
        <span class="poi-popup-icon">${icon}</span>
        <strong>${name || 'Unknown'}</strong>
      </div>
      <div class="poi-popup-content">
        <div><strong>Type:</strong> ${type || amenity || 'N/A'}</div>
  `;
  
  if (status) {
    html += `<div><strong>Status:</strong> ${status}</div>`;
  }
  if (address) {
    html += `<div><strong>Address:</strong> ${address}</div>`;
  }
  if (poi_type) {
    html += `<div><strong>POI Type:</strong> ${poi_type}</div>`;
  }
  if (center_type) {
    html += `<div><strong>Center Type:</strong> ${center_type}</div>`;
  }
  
  html += `
      </div>
    </div>
  `;
  
  return html;
};

/**
 * Create a GeoJSON Polygon approximating a circle on Earth (WGS84).
 * Useful when you want a radius in meters (MapLibre circle radius is in pixels).
 */
export const makeCirclePolygon = (centerLng, centerLat, radiusMeters, steps = 64) => {
  const earthRadiusMeters = 6371008.8;
  const angularDistance = radiusMeters / earthRadiusMeters;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const lat1 = toRad(centerLat);
  const lon1 = toRad(centerLng);

  const coordinates = [];

  for (let i = 0; i <= steps; i++) {
    const bearing = (2 * Math.PI * i) / steps;

    const sinLat1 = Math.sin(lat1);
    const cosLat1 = Math.cos(lat1);
    const sinAd = Math.sin(angularDistance);
    const cosAd = Math.cos(angularDistance);

    const lat2 = Math.asin(sinLat1 * cosAd + cosLat1 * sinAd * Math.cos(bearing));
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin(bearing) * sinAd * cosLat1,
        cosAd - sinLat1 * Math.sin(lat2)
      );

    coordinates.push([toDeg(lon2), toDeg(lat2)]);
  }

  return {
    type: 'Feature',
    properties: { radius_m: radiusMeters },
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates]
    }
  };
};

